import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { HeadingsEnum } from '@inter-ikea-kompis/enums';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { ProductRowLayoutEnum } from '@inter-ikea-kompis/component-product-row';

import { Badge as SkapaBadge } from '@ingka/badge-webc/passive.js';
import * as SkapaStyle from '@ingka/common-styles-webc';

import type IProductListLocalizedInformation from '../../types/IProductListLocalizedInformation.js';
import type IProductListTranslations from '../../types/IProductListTranslations.js';
import ProductListSectionStyle from './ProductListSectionStyle.js';

/**
 * @slot Section content.
 */
export default class ProductListSection extends KompisElement {
	// Theme model
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" instead.
	@property({ type: Object })
	public localizedInformation: IProductListLocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IProductListTranslations | null = null;

	// The section label.
	@property({ type: String })
	public label: string | null = null;

	// The number of items in section.
	@property({ type: Number })
	public numberOfItems: number | null = null;

	// Semantic heading level for the heading with the highest level in the product list section.
	@property({ type: Object })
	public maxHeadingLevel: HeadingsEnum = HeadingsEnum.h2;

	// Layout for product list. NOTE: ProductRowLayoutEnum.compactPrint should only be used during print. Make sure SPR's has their child articles in includedProducts, since SPR's are not visible in compactPrint.
	@property({ type: String })
	public layout: ProductRowLayoutEnum = ProductRowLayoutEnum.default;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		// We want translations to be required, but since all props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		return html`
			<style>
				${ProductListSectionStyle.getStyle({ theme: this.theme, layout: this.layout })}
			</style>
			<div class="label" role="heading" aria-level="${this.maxHeadingLevel}">
				<h2 class="${SkapaStyle.Text.classnames({ heading: this.getHeadingSize() })}">
					${this.label}
				</h2>
				${this.getNumberOfItems()}
			</div>
			<slot></slot>
		`;
	}
	public override async connectedCallback(): Promise<void> {
		this.customElements.define('skapa-badge', SkapaBadge);

		super.connectedCallback();
	}

	private getHeadingSize(): Record<string, boolean> {
		return this.layout === ProductRowLayoutEnum.compactPrint ? { xs: true } : { m: true };
	}

	private hasTranslations(): boolean {
		return !!this.translations || !!this.localizedInformation?.translations;
	}

	private getTranslations(): IProductListTranslations {
		const translations = this.translations
			? this.translations
			: this.localizedInformation?.translations;

		return <IProductListTranslations>translations;
	}

	/**
	 * Renders number of items.
	 *
	 * @returns Template.
	 */
	private getNumberOfItems(): RenderResult {
		if (this.numberOfItems === null) {
			return null;
		}

		return html`
			<skapa-badge class="badge" size="small">
				${this.numberOfItems} ${this.getTranslations().productListItems}
			</skapa-badge>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-product-list-section': ProductListSection;
		'skapa-badge': SkapaBadge;
	}
}
